import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  Divider,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";
import { useContactUsMutation } from "../redux/api/authApi";

interface ContactUsModalProps {
  open: boolean;
  onClose: () => void;
  userDets?: any;
}

const ContactUsModal = ({
  open,
  onClose,
  userDets,
}: ContactUsModalProps) => {
  const [contactUs, { isLoading }] = useContactUsMutation();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    if (open) {
      setForm((prev) => ({
        ...prev,
        name:
          userDets?.name ||
          userDets?.first_name ||
          localStorage.getItem("user_name") ||
          "",
        email: localStorage.getItem("user_email") || "",
        phone: userDets?.phone || "",
      }));
    }
  }, [open, userDets]);

  const handleChange =
    (field: string) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^[0-9]{10,15}$/;

  const handleSubmit = async () => {
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const subject = form.subject.trim();
    const message = form.message.trim();

    if (!name) { toast.error("Please enter your name"); return; }
    if (!email) { toast.error("Please enter your email"); return; }
    if (!emailRegex.test(email)) { toast.error("Please enter a valid email address"); return; }
    if (phone) {
      const cleanedPhone = phone.replace(/\D/g, "");
      if (!phoneRegex.test(cleanedPhone)) {
        toast.error("Phone number must contain 10 to 15 digits");
        return;
      }
    }
    if (!subject) { toast.error("Please enter a subject"); return; }
    if (subject.length < 3) { toast.error("Subject must be at least 3 characters"); return; }
    if (!message) { toast.error("Please enter a message"); return; }
    if (message.length < 10) { toast.error("Message must be at least 10 characters"); return; }

    try {
      const response: any = await contactUs({
        ...form,
        email,
        phone: phone.replace(/\D/g, ""),
      }).unwrap();

      if (response?.status === "success") {
        toast.success(response?.message || "Thanks for contacting us.");
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        onClose();
      }
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to submit request");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: "#0D1526",
          color: "#fff",
          borderRadius: "20px",
          border: "0.5px solid rgba(255,255,255,0.10)",
          overflow: "hidden",
        },
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 3.5,
          pt: 3,
          pb: 2.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          borderBottom: "0.5px solid rgba(255,255,255,0.07)",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              color: "#4BA5EB",
              mb: 0.75,
            }}
          >
            Support
          </Typography>

          <Typography sx={{ fontSize: 20, fontWeight: 600, color: "#fff", lineHeight: 1.2 }}>
            Get in touch
          </Typography>

          <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.40)", mt: 0.5 }}>
            We typically respond within 24 hours
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            mt: 0.25,
            color: "rgba(255,255,255,0.50)",
            border: "0.5px solid rgba(255,255,255,0.10)",
            borderRadius: "8px",
            background: "rgba(255,255,255,0.04)",
            "&:hover": {
              background: "rgba(255,255,255,0.10)",
              color: "#fff",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      {/* ── Body ── */}
      <DialogContent sx={{ px: 3.5, py: 3 }}>
        <Stack spacing={1.5}>

          {/* Name + Phone — side by side */}
          <Stack direction="row" spacing={1.5}>
            <TextField
              label="Full name"
              value={form.name}
              onChange={handleChange("name")}
              fullWidth
              InputLabelProps={{ sx: labelStyle }}
              sx={fieldStyle}
            />

            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "").slice(0, 15);
                setForm((prev) => ({ ...prev, phone: value }));
              }}
              fullWidth
              InputLabelProps={{ sx: labelStyle }}
              sx={fieldStyle}
            />
          </Stack>

          {/* Email — full width */}
          <TextField
            label="Email address"
            value={form.email}
            onChange={handleChange("email")}
            fullWidth
            InputLabelProps={{ sx: labelStyle }}
            sx={fieldStyle}
          />

          <Divider sx={{ borderColor: "rgba(255,255,255,0.07)", my: 0.5 }} />

          {/* Subject */}
          <TextField
            label="Subject"
            value={form.subject}
            onChange={handleChange("subject")}
            fullWidth
            InputLabelProps={{ sx: labelStyle }}
            sx={fieldStyle}
          />

          {/* Message + char counter */}
          <Box>
            <TextField
              label="Message"
              value={form.message}
              onChange={handleChange("message")}
              multiline
              rows={5}
              fullWidth
              inputProps={{ maxLength: 500 }}
              InputLabelProps={{ sx: labelStyle }}
              sx={fieldStyle}
            />
            <Typography
              sx={{
                fontSize: 11,
                color:
                  form.message.length >= 450
                    ? "rgba(235,120,75,0.85)"
                    : "rgba(255,255,255,0.25)",
                textAlign: "right",
                mt: 0.5,
              }}
            >
              {form.message.length} / 500
            </Typography>
          </Box>

          {/* Submit */}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <SendIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              mt: 0.5,
              height: 46,
              borderRadius: "11px",
              background: "linear-gradient(90deg, #0168B5 0%, #4BA5EB 100%)",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              letterSpacing: "0.01em",
              "&:hover": { opacity: 0.9 },
              "&:active": { transform: "scale(0.99)" },
            }}
          >
            {isLoading ? "Sending…" : "Send message"}
          </Button>

          <Typography
            sx={{
              textAlign: "center",
              fontSize: 11,
              color: "rgba(255,255,255,0.22)",
              mt: 0.5,
            }}
          >
            Your info is kept private and never shared.
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const labelStyle = {
  color: "rgba(255,255,255,0.45)",
  fontSize: 13,
};

const fieldStyle = {
  "& .MuiOutlinedInput-root": {
    color: "#fff",
    fontSize: 14,
    borderRadius: "10px",
    background: "rgba(255,255,255,0.04)",
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.10)",
      borderWidth: "0.5px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,255,255,0.22)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4BA5EB",
      borderWidth: "1px",
    },
    "&.Mui-focused": {
      background: "rgba(75,165,235,0.06)",
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "rgba(255,255,255,0.20)",
    opacity: 1,
  },
};

export default ContactUsModal;